// STO-3295: CartBottomBar drawer migrated from custom DrawerScrim + DrawerCard to
// MUI SwipeableDrawer. Swipe-to-dismiss, scroll lock, Escape handling, and focus
// management are all provided by SwipeableDrawer/Modal internals.
import { styled } from '@mui/material/styles'
import Box from '@/components/common/Box'
import {
	colors,
	typography,
	spacing,
	radius,
	sizing,
	focusVisible,
	shadows,
	transitions,
	breakpoints,
} from '@/styles/designTokens'

const BaseButton = styled('button')({
	fontFamily: 'inherit',
	background: 'none',
	border: 'none',
	padding: 0,
	cursor: 'pointer',
})

export const BottomBarOverlay = styled(Box)(({ theme }) => ({
	display: 'flex',
	justifyContent: 'center',
	position: 'fixed',
	bottom: 0,
	left: 0,
	right: 0,
	zIndex: theme.zIndex.stickyHeader + 1,
	pointerEvents: 'none',
	[theme.breakpoints.up('lg')]: {
		display: 'none',
	},
}))

export const BottomBarCard = styled(Box)(({ theme }) => ({
	width: '100%',
	backgroundColor: colors.surfaceWhite,
	borderTop: `1px solid ${colors.border}`,
	borderLeft: 'none',
	borderRight: 'none',
	borderBottom: 'none',
	borderRadius: `${radius.sheet}px ${radius.sheet}px 0 0`,
	filter: `drop-shadow(0px -2px 2.5px ${colors.overlaySubtle})`,
	overflow: 'hidden',
	pointerEvents: 'auto',
	[theme.breakpoints.up('md')]: {
		width: 400,
		border: `1px solid ${colors.brand}`,
		borderRadius: radius.sheet,
		filter: 'none',
		boxShadow: shadows.card,
		margin: `${spacing.lg}px ${spacing.sm}px`,
	},
}))

export const BottomBarInner = styled(Box)({
	display: 'flex',
	flexDirection: 'column',
	gap: spacing.lg,
	padding: spacing.lg,
})

export const BottomBarDetailsRow = styled(Box)({
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'flex-start',
	gap: spacing.lg,
	width: '100%',
	minHeight: sizing.navIcon,
})

export const BottomBarDetailsPairs = styled(Box)({
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'flex-start',
	gap: spacing.sm,
	flex: '1 0 0',
	minWidth: 0,
})

export const BottomBarDetailPair = styled(Box)({
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'center',
	gap: spacing.sm,
	flexShrink: 0,
})

export const BottomBarTimingPair = styled(Box)({
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'flex-start',
	gap: spacing.sm,
	flex: '1 0 0',
	minWidth: 0,
})

export const BottomBarOrderTypeLabel = styled('span')({
	fontSize: typography.fontSizeMd,
	fontWeight: typography.fontWeightBold,
	lineHeight: typography.lineHeightMd,
	letterSpacing: typography.letterSpacingWide,
	color: colors.textPrimary,
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
})

export const BottomBarTimingText = styled('span')({
	fontSize: typography.fontSizeMd,
	fontWeight: typography.fontWeightRegular,
	lineHeight: typography.lineHeightMd,
	letterSpacing: typography.letterSpacingWide,
	color: colors.textPrimary,
})

export const BottomBarTimingBold = styled('span')({
	fontWeight: typography.fontWeightBold,
})

export const BottomBarEditButton = styled(BaseButton)({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	width: sizing.navIcon,
	height: sizing.navIcon,
	color: colors.textPrimary,
	flexShrink: 0,
	'&:focus-visible': {
		outline: `${focusVisible.outline} ${colors.brand}`,
		outlineOffset: focusVisible.outlineOffset,
		borderRadius: radius.badge,
	},
})

export const BottomBarCheckoutRow = styled(Box)({
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'center',
	gap: spacing.sm,
	width: '100%',
})

interface CheckoutButtonProps {
	isDisabled: boolean
}

export const BottomBarCheckoutButton = styled(BaseButton, {
	shouldForwardProp: (prop) => prop !== 'isDisabled',
})<CheckoutButtonProps>(({ isDisabled }) => ({
	flex: '1 0 0',
	height: 40,
	borderRadius: radius.pill,
	backgroundColor: colors.brand,
	color: colors.textInverse,
	fontSize: typography.fontSizeMd,
	fontWeight: typography.fontWeightBold,
	lineHeight: typography.lineHeightLg,
	letterSpacing: typography.letterSpacingWide,
	textTransform: 'uppercase',
	cursor: isDisabled ? 'not-allowed' : 'pointer',
	opacity: isDisabled ? 0.5 : 1,
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	gap: spacing.sm,
	fontVariantNumeric: 'tabular-nums',
	transition: `opacity ${transitions.fast}`,
	'&:focus-visible': {
		outline: `${focusVisible.outline} ${colors.brand}`,
		outlineOffset: focusVisible.outlineOffset,
	},
}))

export const BottomBarItemBadge = styled('span')({
	display: 'inline-flex',
	alignItems: 'center',
	justifyContent: 'center',
	padding: `${spacing.xxs}px ${spacing.sm}px`,
	borderRadius: radius.pill,
	backgroundColor: colors.overlayWhiteSubtle,
	border: `1px solid ${colors.overlayWhiteSubtle}`,
	color: colors.textInverse,
	fontSize: typography.fontSizeSm,
	fontWeight: typography.fontWeightBold,
	lineHeight: typography.lineHeightSm,
	letterSpacing: typography.letterSpacingWidest,
	flexShrink: 0,
})

// SwipeableDrawer Paper styles — passed via slotProps.paper.sx so MUI applies them
// to the internal Paper element without wrapping it in a styled component.
// On mobile: full-width bottom sheet with rounded top corners.
// On tablet (721px+): centred floating card matching the existing BottomBarCard aesthetics.
export const drawerPaperSx = {
	backgroundColor: colors.surfaceWhite,
	borderRadius: `${radius.sheet}px ${radius.sheet}px 0 0`,
	boxShadow: shadows.card,
	width: '100%',
	maxHeight: '90vh',
	display: 'flex',
	flexDirection: 'column',
	overflow: 'hidden',
	[`@media (min-width: ${breakpoints.desktopSm}px)`]: {
		width: 400,
		borderRadius: `${radius.sheet}px`,
		left: 0,
		right: 0,
		bottom: `${spacing.lg}px`,
		marginLeft: 'auto',
		marginRight: 'auto',
		transform: 'none',
	},
} as const

export { DrawerPuller } from '@/components/common/ui/DrawerPuller.styled'

export const DrawerHeader = styled(Box)({
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'center',
	gap: spacing.lg,
	padding: spacing.lg,
	borderBottom: `1px solid ${colors.divider}`,
	flexShrink: 0,
})

export const DrawerHeading = styled('h2')({
	margin: 0,
	flex: '1 1 auto',
	minWidth: 0,
	fontSize: typography.fontSizeXl,
	fontWeight: typography.fontWeightSemiBold,
	lineHeight: typography.lineHeightXl,
	letterSpacing: typography.letterSpacingNormalLg,
	color: colors.textPrimary,
})

export const DrawerHeadingCount = styled('span')({
	color: colors.textSecondary,
})

export const DrawerCloseButton = styled(BaseButton)({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	width: sizing.iconButton,
	height: sizing.iconButton,
	borderRadius: radius.circle,
	color: colors.textPrimary,
	flexShrink: 0,
	'&:focus-visible': {
		outline: `${focusVisible.outline} ${colors.brand}`,
		outlineOffset: focusVisible.outlineOffset,
	},
})

export const DrawerBody = styled(Box)({
	flex: '1 1 auto',
	minHeight: 0,
	// overflow:hidden — DrawerBody must NOT scroll the entire OrderDetailsPanel (which would push
	// CartActionsFooter off-screen). Only ItemList inside PanelRoot scrolls cart items.
	overflow: 'hidden',
	display: 'flex',
	flexDirection: 'column',
})
